import React from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

const SignalBlock: React.FC<{ heading: string; children: React.ReactNode; note?: string }> = ({ heading, children, note }) => (
  <div className="border border-border rounded-md p-6 bg-card">
    <h4
      className="text-lg font-semibold mb-4 text-foreground"
      style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
    >
      {heading}
    </h4>
    {children}
    {note && (
      <p className="text-sm text-muted-foreground italic mt-4">{note}</p>
    )}
  </div>
);

const PatternSignalWorkedExample: React.FC = () => {
  return (
    <section className="my-12" aria-label="Pattern & Signal worked example">
      <h3
        className="text-2xl font-bold mt-8 mb-3"
        style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
      >
        A practical example: how Pattern &amp; Signal works in real terms
      </h3>

      <p className="text-muted-foreground mb-2 text-base leading-relaxed">
        Pattern &amp; Signal is not about predicting outcomes. It observes how small changes accumulate, and highlights when a scenario begins to shift — early enough to matter.
      </p>
      <p className="text-muted-foreground mb-8 text-base leading-relaxed">
        The following is a simplified illustration of how the platform works in practice.
      </p>

      {/* Scenario Title */}
      <div className="mb-6">
        <h4
          className="text-xl font-semibold text-foreground"
          style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
        >
          Scenario: AI Task Redesign &amp; Workforce Pressure
        </h4>
        <p className="text-sm text-muted-foreground italic mt-1">
          Observed initially through a New Zealand lens, with relevance beyond it.
        </p>
      </div>

      <div className="flex flex-col gap-4">
        {/* Block 1 */}
        <SignalBlock heading="Signals observed" note="Individually, none of these signals are decisive.">
          <ul className="space-y-2 text-muted-foreground text-sm leading-relaxed">
            <li className="flex"><span className="mr-2">•</span><span>Businesses rewriting job descriptions to include AI-assisted tasks</span></li>
            <li className="flex"><span className="mr-2">•</span><span>Growing use of AI tools inside existing roles rather than new roles</span></li>
            <li className="flex"><span className="mr-2">•</span><span>Professional services firms adjusting workflow allocation</span></li>
            <li className="flex"><span className="mr-2">•</span><span>Training spend shifting from "skills acquisition" to "task augmentation"</span></li>
          </ul>
        </SignalBlock>

        {/* Block 2 */}
        <SignalBlock heading="Pattern emerging" note="The pattern becomes visible before headline employment data changes.">
          <div className="text-muted-foreground text-sm leading-relaxed space-y-3">
            <p>As these signals repeat across different organisations and sectors, a pattern begins to form.</p>
            <p>This is not job replacement.<br />It is task redistribution inside roles.</p>
          </div>
        </SignalBlock>

        {/* Block 3 */}
        <SignalBlock heading="Scenario movement detected" note="At this stage, no outcome is predicted — only that the operating environment is changing.">
          <div className="text-muted-foreground text-sm leading-relaxed space-y-3">
            <p>Pattern &amp; Signal tracks how this pattern strengthens the AI Task Redesign scenario.</p>
            <p className="font-medium text-foreground/80">Movement observed:</p>
            <ul className="space-y-1">
              <li className="flex"><span className="mr-2">•</span><span>Pressure increasing</span></li>
              <li className="flex"><span className="mr-2">•</span><span>Direction: upward</span></li>
              <li className="flex"><span className="mr-2">•</span><span>Confidence: medium, rising</span></li>
            </ul>
          </div>
        </SignalBlock>

        {/* Block 4 */}
        <SignalBlock heading="Why this matters for decision-makers">
          <ul className="space-y-2 text-muted-foreground text-sm leading-relaxed">
            <li className="flex"><span className="mr-2">•</span><span>Workforce planning assumptions may need revision</span></li>
            <li className="flex"><span className="mr-2">•</span><span>Productivity gains may arrive unevenly across teams</span></li>
            <li className="flex"><span className="mr-2">•</span><span>Hiring freezes or expansions based on old role definitions may misfire</span></li>
            <li className="flex"><span className="mr-2">•</span><span>Training strategy becomes a near-term decision, not a future one</span></li>
          </ul>
          <p className="text-sm text-foreground/80 font-medium mt-4">
            This information is useful before the change becomes obvious.
          </p>
        </SignalBlock>
      </div>

      {/* Closing */}
      <p className="text-base text-muted-foreground mt-8 leading-relaxed" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
        Pattern &amp; Signal exists to help decision-makers stay oriented — not to chase forecasts.
      </p>

      {/* Call-out box */}
      <div className="mt-6 border border-border/60 rounded-md px-5 py-4 bg-muted/30">
        <p
          className="text-sm text-muted-foreground italic text-center"
          style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
        >
          "This same process runs continuously across multiple scenarios — energy, credit, labour, infrastructure, and regulatory pressure."
        </p>
      </div>
    </section>
  );
};

export default PatternSignalWorkedExample;
